import { Link } from "react-router-dom";
import { Badge, Card, CardBody, formatDate } from "@odingard/design-system";
import { ErrorState, LoadingState, EmptyState } from "@odingard/design-system";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { usePageMeta } from "../../lib/usePageMeta";

interface UpdateEntry {
  slug: string;
  appName: string;
  icon: string;
  version: string;
  isCurrent: boolean;
  notes: string;
  releasedAt: string;
}

export function MarketplaceUpdates() {
  usePageMeta({
    title: "Marketplace updates | Odingard Studio",
    description: "Release notes and lifecycle changes across every application in the Odingard Studio catalog.",
    path: "/marketplace/updates",
  });
  const state = useAsync(async () => {
    const { applications } = await api.catalogApplications({ status: "available", pageSize: 100 });
    const details = await Promise.all(applications.map((a) => api.catalogApplication(a.slug)));
    const entries: UpdateEntry[] = [];
    for (const app of details) {
      for (const v of app.versions) {
        entries.push({
          slug: app.slug,
          appName: app.name,
          icon: app.icon,
          version: v.version,
          isCurrent: v.isCurrent,
          notes: v.notes,
          releasedAt: v.releasedAt,
        });
      }
    }
    entries.sort((a, b) => new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime());
    return entries;
  }, []);

  const entries = state.data ?? [];

  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: "var(--ods-space-5)" }}>
          <Link to="/marketplace" style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
            ← Back to Marketplace
          </Link>
        </div>
        <div className="section__head">
          <h1>What&apos;s new in Odingard Studio</h1>
          <p>Release history across every available Odingard Studio application.</p>
        </div>

        {state.loading && <LoadingState label="Loading updates…" />}
        {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
        {state.data && (
          entries.length === 0 ? (
            <EmptyState title="No updates yet" description="Release notes will appear here as applications ship new versions." />
          ) : (
            <div style={{ display: "grid", gap: "var(--ods-space-4)" }}>
              {entries.map((e) => (
                <Card key={`${e.slug}-${e.version}`}>
                  <CardBody>
                    <div className="ods-row" style={{ gap: "var(--ods-space-3)", alignItems: "center", flexWrap: "wrap" }}>
                      <span aria-hidden="true" style={{ fontSize: "1.5rem" }}>
                        {e.icon}
                      </span>
                      <Link to={`/marketplace/apps/${e.slug}`} style={{ fontWeight: 600 }}>
                        {e.appName}
                      </Link>
                      <Badge tone={e.isCurrent ? "success" : "neutral"}>v{e.version}</Badge>
                      <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginLeft: "auto" }}>
                        {formatDate(e.releasedAt)}
                      </span>
                    </div>
                    <p style={{ margin: "var(--ods-space-3) 0 0" }}>{e.notes}</p>
                  </CardBody>
                </Card>
              ))}
            </div>
          )
        )}
      </div>
    </section>
  );
}
